import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * Dialog pentru setarea / eliminarea override-ului de necesar la nivel de Client (Auftraggeber)
 * pe un INTERVAL (de la → până la).
 */
export function ClientOverrideDialog({ clientName, defaultStart, defaultEnd, defaultValue, onClose, onSaved }) {
  const [start, setStart] = useState(defaultStart || new Date().toISOString().slice(0, 10));
  const [end, setEnd] = useState(defaultEnd || defaultStart || new Date().toISOString().slice(0, 10));
  const [val, setVal] = useState(defaultValue != null ? String(defaultValue) : "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!start || !end) { toast.error("Selectează data de început și de final"); return; }
    if (end < start) { toast.error("Data finală trebuie >= data de început"); return; }
    const n = parseInt(val);
    if (isNaN(n) || n < 0) { toast.error("Introduce un număr valid (>=0)"); return; }
    setBusy(true);
    try {
      await api.post("/management/client-overrides/range", {
        client_name: clientName,
        start_date: start,
        end_date: end,
        min_people: n,
      });
      toast.success(`Override aplicat pentru ${clientName}: ${n} pers/zi`);
      onSaved && onSaved();
    } catch (e) {
      toast.error("Eroare la salvare");
    } finally {
      setBusy(false);
    }
  };

  const clearRange = async () => {
    if (!start || !end) return;
    if (!window.confirm(`Șterg toate override-urile pentru ${clientName} între ${start} și ${end}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/management/client-overrides/range?client_name=${encodeURIComponent(clientName)}&start_date=${start}&end_date=${end}`);
      toast.success("Override-uri șterse");
      onSaved && onSaved();
    } catch (e) {
      toast.error("Eroare la ștergere");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="client-override-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>👔 Necesar fix pentru client</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs">
            <div className="font-bold text-amber-900">{clientName}</div>
            <div className="text-amber-700 mt-1">
              Setează un număr fix de persoane/zi pentru toate proiectele și regiile acestui client.
              Acest număr <strong>înlocuiește</strong> suma individuală pe regii/proiecte în „Total necesar".
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">De la</Label>
              <Input data-testid="client-override-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Până la (inclusiv)</Label>
              <Input data-testid="client-override-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Persoane/zi</Label>
            <Input
              data-testid="client-override-value"
              type="number"
              min="0"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              placeholder="ex: 10"
              autoFocus
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Anulează</Button>
          <Button variant="outline" onClick={clearRange} disabled={busy} className="text-red-600 hover:bg-red-50" data-testid="client-override-clear">
            Șterge interval
          </Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid="client-override-save">
            {busy ? "Salvez…" : "Salvează"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
